import { DropdownData, InputType } from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import {
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { environment } from 'src/environments/environment';

export class ProductosGanaderiaFormDefinition {
    public appName = environment.appName;
    public showPrecio = environment.features.showPrecio;
    public formType!: FormType;
    public model: Partial<ProductosModel> = {};
    public formFields!: Form<ProductosModel>;
    public fieldNames: FormField<ProductosModel>[] = [];
    public fieldLabels: string[] = [];

    public unidades: DropdownData<string> = {
        selected: null,
        filtered: [
            {label: '...', value: null},
            {label: 'unidades', value: 'unidades'},
            {label: 'sobres', value: 'sobres'},
            {label: 'sacos', value: 'sacos'},
            {label: 'toneladas', value: 'toneladas'},
            {label: 'gr', value: 'gr'},
            {label: 'kilos', value: 'kilos'},
            {label: 'litros', value: 'litros'},
            {label: 'c.c.', value: 'c.c.'},
            {label: 'm2.', value: 'm2'},
        ],
        values: []
    };

    public tipos: DropdownData<string> = {
        selected: null,
        filtered: [
            {label: '...', value: null},
            {label: 'Plantas', value: 'plantas'},
            {label: 'Plantas Acuáticas', value: 'plantas_acuaticas'},
            {label: 'Árboles', value: 'arboles'},
        ],
        values: []
    };

    constructor(type: FormType = FormType.CREATION, model: Partial<ProductosModel> = {}) {
        this.formType = +type;
        this.model = model;

        this.formFields = {
            name: 'productos-ganaderia',
            header: `Datos del producto de ${environment.features.nameModuloGanaderia}`,
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }

    private getFormSections(): FormSection<ProductosModel>[] {
        return [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: false,
                fields: this.getHiddenFields(),
                order: 2,
            },
        ];
    }

    private getMainFields(): FormField<ProductosModel>[] {
        const isEditable = this.formType < FormType.DELETION;
        const textInputType = isEditable ? InputType.TEXT : InputType.ONLY_TEXT;
        const numericInputType = isEditable ? InputType.NUMERIC : InputType.ONLY_TEXT;
        const dropdownInputType = isEditable ? InputType.DROPDOWN : InputType.ONLY_TEXT;
        const textAreaInputType = isEditable ? InputType.TEXTAREA : InputType.ONLY_TEXT;

        const fields: FormField<ProductosModel>[] = [
            {
                name: 'nombre',
                vinculatedFields: undefined,
                label: 'Nombre',
                input: {
                    type: textInputType,
                    isEditable,
                },
                width: 6,
                conditions: this.conditions({ isRequired: true }),
                order: 1,
            },
            {
                name: 'precio',
                vinculatedFields: undefined,
                label: 'Precio',
                input: {
                    type: numericInputType,
                    isEditable,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.showPrecio }),
                order: 2,
            },
            {
                name: 'unidades',
                vinculatedFields: undefined,
                label: 'Unidades medida',
                input: {
                    type: dropdownInputType,
                    data: this.unidades,
                    isEditable,
                },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'tipo',
                vinculatedFields: undefined,
                label: 'Tipo',
                input: {
                    type: dropdownInputType,
                    data: this.tipos,
                    isEditable,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'detorres' }),
                order: 4,
            },
            {
                name: 'observaciones',
                vinculatedFields: undefined,
                label: 'Observaciones',
                input: {
                    type: textAreaInputType,
                    isEditable,
                    textAreaConfiguration: {
                        hasVoiceTranscription: false,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 12,
                conditions: this.conditions(),
                order: 5,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getHiddenFields(): FormField<ProductosModel>[] {
        const fields: FormField<ProductosModel>[] = [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: 'ID',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: true,
                },
                width: 0,
                conditions: this.conditions({ isVisible: false }),
                order: 1,
            },
            {
                name: 'id_cliente',
                vinculatedFields: undefined,
                label: 'Cliente',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: true,
                },
                width: 0,
                conditions: this.conditions({ isVisible: false }),
                order: 2,
            },
        ];

        if (this.appName !== 'detorres') {
            fields.push({
                name: 'tipo',
                vinculatedFields: undefined,
                label: 'Tipo',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: true,
                    defaultValue: 'ganaderia',
                },
                updateValue: () => this.model.tipo ?? 'ganaderia',
                width: 0,
                conditions: this.conditions({ isVisible: true }),
                order: 3,
            });
        }

        return fields;
    }

    private registerVisibleFields(fields: FormField<ProductosModel>[]): FormField<ProductosModel>[] {
        for (const field of fields) {
            if (field.conditions.isVisible) {
                this.fieldNames.push(field);
                this.fieldLabels.push(field.label);
            }
        }

        return fields.sort((a, b) => a.order - b.order);
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: false,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
